/**
 * WS-14 — OBSERVABILITY: the console-side error reporter.
 *
 * `reportError` builds a SMALL, strictly PII-FREE technical payload and ships it to the API's LOG-ONLY
 * ingest (POST /observability/client-error) via `navigator.sendBeacon`, falling back to a keepalive
 * `fetch`. It is FIRE-AND-FORGET and fully wrapped in try/catch: a failed report must NEVER throw,
 * never re-break the UI, and never loop (a report that itself errored would otherwise re-trigger the
 * window 'error'/'unhandledrejection' handlers that called us).
 *
 * The payload carries ONLY: message, a capped stack, an optional componentStack, the url, the release
 * tag, an optional requestId (lifted from an ApiClientError so a client report joins the exact server
 * error envelope it came from), and a kind. It NEVER includes form values, client/customer data,
 * cookies, or auth — the API side (modules/observability/routes.ts) is the matching contract and caps
 * every field again as defence-in-depth.
 *
 * Two guards stop a render loop or React StrictMode double-invoke from spamming the ingest:
 *   • DEDUPE — a per-session Set of recent message+stack hashes; an identical error is sent at most once.
 *   • CAP — at most REPORT_CAP reports per page session, full stop.
 */
import { ApiClientError } from './api.js';
import { env } from './env.js';

/** Field caps (must stay <= the API-side caps). Keep payloads tiny and the ingest body well under 16 KiB. */
const MESSAGE_MAX = 2_000;
const STACK_MAX = 8_000;
const COMPONENT_STACK_MAX = 8_000;

/** Per-session hard cap on reports — a runaway render loop can never flood the ingest. */
const REPORT_CAP = 20;

/** Recent (message+stack) signatures already sent this session — dedupes StrictMode/loop double-fires. */
const seen = new Set<string>();
/** How many reports we've sent this session (counts only NEW, deduped sends against REPORT_CAP). */
let sentCount = 0;

interface ReportContext {
  kind?: string;
  componentStack?: string;
}

interface ClientErrorPayload {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  release: string;
  requestId?: string;
  kind?: string;
}

function cap(value: string | undefined, max: number): string | undefined {
  if (value === undefined) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

/** Cheap, dependency-free 32-bit string hash (djb2) — only used to key the in-memory dedupe Set. */
function hash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = (h * 33) ^ input.charCodeAt(i);
  }
  // >>> 0 → unsigned; base36 keeps the key short.
  return (h >>> 0).toString(36);
}

/** Normalise any thrown value into a message + (capped) stack, lifting an ApiClientError's requestId. */
function describe(error: unknown): {
  message: string;
  stack?: string;
  requestId?: string;
} {
  if (error instanceof ApiClientError) {
    return {
      message: cap(error.message, MESSAGE_MAX) ?? 'ApiClientError',
      stack: cap(error.stack, STACK_MAX),
      requestId: error.requestId,
    };
  }
  if (error instanceof Error) {
    return {
      message: cap(error.message, MESSAGE_MAX) ?? error.name,
      stack: cap(error.stack, STACK_MAX),
    };
  }
  return { message: cap(String(error), MESSAGE_MAX) ?? 'Unknown error' };
}

/** POST the payload best-effort: sendBeacon first (survives unload), keepalive fetch as the fallback. */
function send(payload: ClientErrorPayload): void {
  const url = `${env.apiBaseUrl}/observability/client-error`;
  const json = JSON.stringify(payload);

  // sendBeacon is the primary path: it is non-blocking and survives a page unload (an error that
  // immediately navigates away). It returns false if it could not queue the send (e.g. payload too big).
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([json], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) return;
  }

  // Fallback: keepalive fetch (also survives unload). credentials:'include' matches the API client so a
  // same-site deployment stays first-party; the ingest is unauthenticated, so this carries no auth weight.
  // Errors are swallowed — a failed report must never surface.
  void fetch(url, {
    method: 'POST',
    body: json,
    keepalive: true,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  }).catch(() => {});
}

/**
 * Report a client error. FIRE-AND-FORGET — never throws, never returns a value the caller must handle.
 */
export function reportError(error: unknown, context?: ReportContext): void {
  try {
    const { message, stack, requestId } = describe(error);

    // Dedupe + per-session cap BEFORE building/sending, so a render loop is cheap and silent.
    const signature = hash(`${context?.kind ?? ''}|${message}|${stack ?? ''}`);
    if (seen.has(signature)) return;
    if (sentCount >= REPORT_CAP) return;
    seen.add(signature);
    sentCount += 1;

    const payload: ClientErrorPayload = {
      message,
      stack,
      componentStack: cap(context?.componentStack, COMPONENT_STACK_MAX),
      // ONLY origin + pathname — NEVER location.href: its query string carries LIVE CREDENTIALS
      // (?token= on accept-invite / reset-password) and customer PII (?q=<search>), and this report
      // is unauthenticated + logged. Stripping the query + hash keeps it PII-free even on the
      // pre-login pages the ingest exists for.
      url: typeof location !== 'undefined' ? location.origin + location.pathname : '',
      release: import.meta.env.VITE_RELEASE ?? 'dev',
      requestId,
      kind: context?.kind,
    };

    send(payload);
  } catch {
    // A reporter that throws would re-break the UI / re-enter the window error handlers — swallow.
  }
}

/** Test-only: reset the in-memory dedupe Set + session counter between cases. */
export function __resetReportStateForTests(): void {
  seen.clear();
  sentCount = 0;
}

let initialised = false;

/**
 * Register global window listeners that funnel uncaught errors + unhandled promise rejections into
 * `reportError`. Call ONCE from main.tsx before render; the `initialised` guard makes a second call
 * (e.g. under React StrictMode's double-invoke) a no-op so listeners are never doubled.
 */
export function initErrorReporting(): void {
  if (initialised) return;
  if (typeof window === 'undefined') return;
  initialised = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Prefer the real Error object (has a stack); fall back to the message string.
    reportError(event.error ?? event.message, { kind: 'window-error' });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    // event.reason is whatever was rejected — often an ApiClientError, whose requestId describe() lifts.
    reportError(event.reason, { kind: 'unhandledrejection' });
  });
}
